import {
  type AbsenceMark,
  addDays,
  type GradeRow,
  type Journal,
  type JournalLesson,
  type JournalStudent,
  dayMonth,
  gradeLevel,
  gradeValue,
  isCovered,
  scaleFor,
  shortDate,
  termFor,
  timeOf,
  todayIso,
  useJournal,
  useCourseAbsenceNotes,
  useClassTests,
  useJournalActions,
  useJournalConflicts,
  useJournalMarks,
  useJournalPending,
  useResolveJournalConflict,
  useIsOnline,
  gradeColumns,
  type JournalConflict,
  useLessonPresence,
  useMarkAbsentByPresence,
  useTerms,
  lessonFit,
  useAbroadStudents,
} from '@shkola/api';
import {
  type AssessmentKind,
  type GradeValue,
  LEVELS,
  NATIONAL_RULES_2026,
  type ScaleCode,
  type SpecialMark,
  VERBAL_VALUES,
  resolveRule,
  suggestFinal,
} from '@shkola/core';
import { subjectLook } from '@shkola/design';
import { Check, WifiOff } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { placeOf, thereNow } from '../student/AbroadPage';
import { useMinute } from '../lib/now';
import { useCoursePicker } from '../components/CoursePicker';
import { Button, EmptyState, Field, Modal, Segmented, Spinner } from '../components/ui';
import {
  DEEP,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  SUBJECT_GRAD,
  SubjectArt,
  Tabs,
  grad,
} from '../design/forms';
import { WarningCircleIcon } from '../design/icons';
import { buttonClass } from '../design/ui';
import { gradeLong, gradeShort } from '../lib/labels';

/*
 * «Журнал» in «Форми 2» (28.10). A working tool the teacher opens every day, so it stays a dense table —
 * the students down, the lessons across, the thematic, the average and the term at the right. Only the
 * look moves into the language of the student's grades: a grade is a small soft mark of its level's
 * colour (coral 1–3, butter 4–6, sky 7–9, lime 10–12, lavender for thematic and term), a
 * test has an orange dot, an absence is a letter in coral; the heads are light, today in orange.
 */

/** the colour of a level: low coral, then butter, sky, lime; thematic and term grades lavender */
const LEVEL: ReadonlyArray<Grad> = ['coral', 'butter', 'sky', 'lime'];
const FINAL = new Set<string>(['thematic', 'semester', 'year']);
const TODAY_INK = '#E8551F';
const TEST_DOT = GRAD.orange.to;
const FEEDBACK_DOT = DEEP.cobalt[1];
const FORMAT_DOT = { online: DEEP.lavender[1], shelter: DEEP.butter[1] } as const;
/** present or why not, by colour */
const ATTENDANCE: Record<AbsenceMark | 'present', Grad> = {
  present: 'mint',
  absent: 'coral',
  alarm: 'butter',
  technical: 'stone',
};
/** the thematic, the average and the term: one sticky cell at the right, three fixed parts */
const FINALS = {
  thematic: 'block w-[124px] shrink-0 px-2',
  average: 'block w-14 shrink-0',
  semester: 'block w-14 shrink-0',
} as const;

const toneOf = (kind: string, value: GradeValue | null, special?: string | null): Grad =>
  FINAL.has(kind)
    ? 'lavender'
    : special || !value
      ? 'stone'
      : (LEVEL[gradeLevel(value)] ?? 'stone');

/** A grade as a small soft mark on its level's colour; a test has an orange dot. */
function Mark({
  tone,
  children,
  test = false,
  title,
  size = 'sm',
}: {
  tone: Grad;
  children: ReactNode;
  test?: boolean;
  title?: string;
  size?: 'sm' | 'xs';
}) {
  return (
    <span
      title={title}
      className={`relative inline-flex shrink-0 items-center justify-center rounded-[8px] px-1 leading-none font-semibold tabular-nums ${size === 'xs' ? 'h-6 min-w-6 text-[12px]' : 'h-7 min-w-7 text-[14px]'}`}
      style={{ backgroundImage: grad(tone), color: INK }}
    >
      {children}
      {test && (
        <span
          aria-hidden
          className="absolute -top-0.5 -right-0.5 size-[7px] rounded-full"
          style={{ backgroundColor: TEST_DOT, boxShadow: '0 0 0 1.5px #FFFFFF' }}
        />
      )}
    </span>
  );
}

/** A soft dot with its words, for the legend. */
function Dot({ color, children }: { color: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="size-1.5 rounded-full" style={{ backgroundColor: color }} />
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------------------------
// Grade value picker (scale-aware)
// ---------------------------------------------------------------------------------------------
function GradePicker({
  scale,
  current,
  final = false,
  onPick,
}: {
  scale: ScaleCode;
  current: GradeValue | null;
  /** a thematic or a term grade: lavender, as in the journal */
  final?: boolean;
  onPick: (value: GradeValue | { special: SpecialMark }) => void;
}) {
  const { t } = useTranslation();
  const isCurrent = (v: GradeValue) => JSON.stringify(v) === JSON.stringify(current);
  const button = (value: GradeValue, label: string, title?: string) => {
    const tone: Grad = final ? 'lavender' : (LEVEL[gradeLevel(value)] ?? 'stone');
    const on = isCurrent(value);
    return (
      <button
        key={label}
        type="button"
        title={title}
        aria-pressed={on}
        onClick={() => onPick(value)}
        className={`rounded-[14px] transition-[filter] hover:brightness-[0.97] ${
          scale === 'points12'
            ? 'h-12 text-[20px] leading-none font-semibold tabular-nums'
            : 'min-h-11 px-3.5 py-2 text-left text-[14px] leading-5 font-medium'
        }`}
        style={{
          backgroundImage: grad(tone),
          color: scale === 'points12' ? INK : GRAD[tone].ink,
          boxShadow: on ? `inset 0 0 0 2px var(--sh-dark)` : undefined,
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div className="flex flex-col gap-3">
      <div
        className={
          scale === 'points12'
            ? 'grid grid-cols-6 gap-1.5'
            : 'grid grid-cols-1 gap-1.5 sm:grid-cols-2'
        }
      >
        {scale === 'points12' &&
          Array.from({ length: 12 }, (_, i) =>
            button({ scale: 'points12', points: i + 1 }, String(i + 1)),
          )}
        {scale === 'levels4' &&
          LEVELS.map((level) =>
            button({ scale: 'levels4', level }, `${level} — ${t(`grading.levels.${level}`)}`),
          )}
        {scale === 'verbal' &&
          VERBAL_VALUES.map((verbal) =>
            button({ scale: 'verbal', verbal }, t(`grading.verbal.${verbal}`)),
          )}
        {scale === 'pass_fail' && (
          <>
            {button({ scale: 'pass_fail', passed: true }, t('grading.passed'))}
            {button({ scale: 'pass_fail', passed: false }, t('grading.failed'))}
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(['not_assessed', 'exempt'] as const).map((special) => (
          <button
            key={special}
            type="button"
            onClick={() => onPick({ special })}
            className={buttonClass('outline', 'sm')}
          >
            {t(`journal.special.${special}`)} — {t(`journal.specialFull.${special}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

/** A small name over a part of an editor. */
function Label({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-[13px] font-medium" style={{ color: INK_SOFT }}>
      {children}
    </p>
  );
}

/** A box of the paper inside a dialog: a hint, the system's suggestion. */
const note = 'rounded-[16px] px-3.5 py-3 text-[14px] leading-5';

/** «Контрольна робота» as a check of the paper. */
function TestCheck({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <label className="flex cursor-pointer items-center gap-2.5 text-[14px]" style={{ color: INK }}>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="size-[18px] rounded-[5px] accent-[var(--sh-dark)]"
      />
      {t('journal.isTest')}
      <span aria-hidden className="size-1.5 rounded-full" style={{ backgroundColor: TEST_DOT }} />
    </label>
  );
}

/** Other tests of the class that day: a butter warning from the third one, a quiet line for the second. */
function TestsThatDay({
  others,
  classLabel,
}: {
  others: ReadonlyArray<{ subject: string }>;
  classLabel: string;
}) {
  const { t } = useTranslation();
  const subjects = others.map((l) => l.subject).join(', ');
  if (others.length >= 2)
    return (
      <p
        className={`${note} flex items-start gap-2`}
        style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
      >
        <WarningCircleIcon size={18} weight="fill" aria-hidden className="mt-px shrink-0" />
        <span style={{ color: INK }}>
          {t('journal.testsTooMany', { class: classLabel, count: others.length, subjects })}
        </span>
      </p>
    );
  if (others.length === 1)
    return (
      <p className="text-[13.5px]" style={{ color: INK_SOFT }}>
        {t('journal.testsOne', { class: classLabel, subjects })}
      </p>
    );
  return null;
}

// ---------------------------------------------------------------------------------------------
// Editors
// ---------------------------------------------------------------------------------------------
function CellEditor({
  journal,
  schoolId,
  student,
  lesson,
  onClose,
}: {
  journal: Journal;
  schoolId: string;
  student: JournalStudent;
  lesson: JournalLesson;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const existing = journal.grades.find(
    (g) => g.lesson_id === lesson.id && g.student_member_id === student.id,
  );
  const absence = journal.attendance.find(
    (a) => a.lesson_id === lesson.id && a.student_member_id === student.id,
  );
  const [kind, setKind] = useState<AssessmentKind>(existing?.kind ?? 'current');
  const scale = scaleFor(journal.course.grade, kind);
  const formative = journal.formative.filter(
    (f) => f.lesson_id === lesson.id && f.student_member_id === student.id,
  );

  // without connection the marks wait on the device (and replace each other in the same cell)
  const marks = useJournalMarks();
  const pending = useJournalPending(journal.course.id);
  const cell = `${lesson.id}|${student.id}`;
  const queuedGrade = pending.grades.get(cell);
  const queuedAbsence = pending.absences.get(cell);
  const seen = existing ? { id: existing.id, updatedAt: existing.updated_at } : null;
  const base = {
    schoolId,
    courseId: journal.course.id,
    termId: journal.termId,
    lessonId: lesson.id,
    studentId: student.id,
    seen,
  };
  const pick = (value: GradeValue | { special: SpecialMark }) => {
    marks.setGrade({
      ...base,
      grade: {
        kind: kind as 'current' | 'test',
        scale,
        columns: gradeColumns(value),
        ...(kind === 'test' && { title: t('journal.kinds.test') }),
      },
    });
    onClose();
  };
  const shownGrade = queuedGrade
    ? queuedGrade.grade &&
      gradeValue({ scale: queuedGrade.grade.scale, ...queuedGrade.grade.columns })
    : existing
      ? gradeValue(existing)
      : null;
  const shownMark = queuedAbsence ? queuedAbsence.mark : (absence?.mark ?? null);

  return (
    <Modal
      title={`${student.lastName} ${student.firstName} · ${shortDate(lesson.date)}`}
      onClose={onClose}
    >
      <p className="-mt-2 mb-5 text-[14px]" style={{ color: INK_SOFT }}>
        {lesson.topic ?? t('journal.topicPlaceholder')}
      </p>
      <div className="flex flex-col gap-5">
        <div>
          <Label>{t('journal.editor.attendance')}</Label>
          {/* present or why not: soft chips, the chosen one in its colour */}
          <div className="flex flex-wrap gap-1.5" role="radiogroup">
            {(['present', 'absent', 'alarm', 'technical'] as const).map((m) => {
              const on = (shownMark ?? 'present') === m;
              const tone = ATTENDANCE[m];
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() =>
                    marks.setAbsence({
                      schoolId,
                      courseId: journal.course.id,
                      lessonId: lesson.id,
                      studentId: student.id,
                      mark: m === 'present' ? null : m,
                      seen: absence?.mark ?? null,
                    })
                  }
                  className={`inline-flex h-10 items-center rounded-full px-4 text-[14px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`}
                  style={
                    on
                      ? {
                          backgroundImage: grad(tone),
                          color: GRAD[tone].ink,
                          boxShadow: `inset 0 0 0 1.5px ${GRAD[tone].ink}`,
                        }
                      : { backgroundColor: PAPER, color: INK }
                  }
                >
                  {m === 'present' ? t('journal.editor.present') : t(`journal.absenceFull.${m}`)}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[13px] font-medium" style={{ color: INK_SOFT }}>
              {t('journal.editor.grade')}
            </p>
            <Segmented<AssessmentKind>
              value={kind}
              onChange={setKind}
              options={(['current', 'test'] as const).map((k) => ({
                value: k,
                label: t(`journal.kinds.${k}`),
              }))}
            />
          </div>
          <GradePicker scale={scale} current={shownGrade} onPick={pick} />
        </div>
        {formative.length > 0 && (
          <div>
            <Label>{t('journal.editor.formative')}</Label>
            {formative.map((f) => (
              <p
                key={f.id}
                className={note}
                style={{ backgroundImage: grad('cobalt'), color: INK }}
              >
                {f.sticker && (
                  <span className="font-medium" style={{ color: GRAD.cobalt.ink }}>
                    {t(`stickers.${f.sticker as 'star'}`).replace(/^[^\p{L}]+/u, '')}{' '}
                  </span>
                )}
                {f.comment}
              </p>
            ))}
          </div>
        )}
        {(queuedGrade ? queuedGrade.grade : existing) && (
          <div>
            <button
              type="button"
              className="text-[14px] font-medium underline underline-offset-4"
              style={{ color: GRAD.coral.ink }}
              onClick={() => {
                marks.setGrade({ ...base, grade: null });
                onClose();
              }}
            >
              {t('journal.editor.clear')}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Who was in the video room of an online lesson, and «н» for those who never came. */
function OnlinePresence({ journal, lesson }: { journal: Journal; lesson: JournalLesson }) {
  const { t } = useTranslation();
  const presence = useLessonPresence(lesson.id, lesson.date === todayIso());
  const mark = useMarkAbsentByPresence();
  if (presence.isPending) return <Spinner />;
  return (
    <div className="mt-6 border-t pt-5" style={{ borderColor: LINE }}>
      <Label>{t('lesson.presence.title')}</Label>
      <ul className="flex max-h-64 flex-col overflow-y-auto text-[14px]">
        {journal.students.map((s) => {
          const p = presence.data?.get(s.id);
          return (
            <li
              key={s.id}
              className="flex justify-between gap-3 border-b py-1.5 last:border-0"
              style={{ borderColor: LINE, color: INK }}
            >
              <span>
                {s.lastName} {s.firstName}
              </span>
              <span
                className="text-right tabular-nums"
                style={{
                  color: !p ? GRAD.coral.ink : p.online ? GRAD.mint.ink : INK_SOFT,
                }}
              >
                {!p
                  ? t('lesson.presence.never')
                  : p.online
                    ? t('lesson.presence.inRoom', { time: timeOf(p.firstJoinedAt) })
                    : t('lesson.presence.joined', {
                        from: timeOf(p.firstJoinedAt),
                        to: timeOf(p.lastLeftAt as string),
                        minutes: p.minutes,
                      })}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-[12.5px]" style={{ color: INK_SOFT }}>
        {t('lesson.presence.hint')}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={mark.isPending}
          onClick={() => mark.mutate(lesson.id)}
          className={`${buttonClass('outline')} disabled:opacity-50`}
        >
          {t('lesson.presence.markAbsent')}
        </button>
        {mark.isSuccess && (
          <span
            className="rounded-[7px] px-2 py-1 text-[12.5px] font-medium"
            style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
          >
            {t('lesson.presence.marked', { count: mark.data })}
          </span>
        )}
      </div>
    </div>
  );
}

function TopicEditor({
  journal,
  lesson,
  onClose,
}: {
  journal: Journal;
  lesson: JournalLesson;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const actions = useJournalActions(journal.course.id, journal.termId);
  const [topic, setTopic] = useState(lesson.topic ?? '');
  const [bring, setBring] = useState(lesson.bring ?? '');
  const [isTest, setIsTest] = useState(lesson.isTest);
  const tests = useClassTests(journal.course.classId, lesson.date);
  const others = (tests.data ?? []).filter((l) => l.id !== lesson.id);
  return (
    <Modal title={`${t('journal.editTopic')} · ${shortDate(lesson.date)}`} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void actions.setTopic
            .mutateAsync({ lessonId: lesson.id, topic, bring, isTest })
            .then(onClose);
        }}
      >
        <Field
          id="topic"
          label={t('journal.editTopic')}
          value={topic}
          maxLength={300}
          onChange={(e) => setTopic(e.target.value)}
        />
        <Field
          id="bring"
          label={t('journal.bring')}
          placeholder={t('journal.bringHint')}
          value={bring}
          maxLength={200}
          onChange={(e) => setBring(e.target.value)}
        />
        <TestCheck value={isTest} onChange={setIsTest} />
        {isTest && <TestsThatDay others={others} classLabel={journal.course.classLabel} />}
        <div>
          <Button type="submit" className="!w-auto px-6">
            {t('journal.editor.save')}
          </Button>
        </div>
      </form>
      {lesson.format === 'online' && lesson.date <= todayIso() && (
        <OnlinePresence journal={journal} lesson={lesson} />
      )}
    </Modal>
  );
}

/** Semester (with the system's suggestion) or a new thematic grade for one student. */
function FinalEditor({
  journal,
  schoolId,
  student,
  kind,
  onClose,
}: {
  journal: Journal;
  schoolId: string;
  student: JournalStudent;
  kind: 'thematic' | 'semester';
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const actions = useJournalActions(journal.course.id, journal.termId);
  const scale = scaleFor(journal.course.grade, kind);
  const mine = journal.grades.filter((g) => g.student_member_id === student.id);
  const existing = kind === 'semester' ? mine.find((g) => g.kind === 'semester') : undefined;
  const [title, setTitle] = useState('');
  const rule = resolveRule(NATIONAL_RULES_2026, {
    academicYear: '2026/2027',
    grade: journal.course.grade,
    kind,
  });
  const suggestion =
    rule &&
    suggestFinal(
      kind,
      mine.map((g) => ({
        kind: g.kind,
        value: gradeValue(g),
        ...(g.special && { special: g.special as SpecialMark }),
      })),
      rule,
    );
  return (
    <Modal
      title={`${t(`journal.kinds.${kind}`)} · ${student.lastName} ${student.firstName}`}
      onClose={onClose}
    >
      <div className={`${note} mb-4 flex items-center gap-3`} style={{ backgroundColor: PAPER }}>
        {suggestion && <Mark tone="lavender">{gradeShort(t, suggestion.value)}</Mark>}
        <span style={{ color: INK }}>
          {suggestion
            ? t('journal.suggestion', {
                value: gradeLong(t, suggestion.value),
                raw: suggestion.raw.toFixed(2),
              })
            : t('journal.noSuggestion')}
        </span>
      </div>
      {kind === 'thematic' && (
        <div className="mb-4">
          <Field
            id="title"
            label={t('journal.editTopic')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
      )}
      <GradePicker
        scale={scale}
        final
        current={existing ? gradeValue(existing) : null}
        onPick={(value) =>
          void actions.saveGrade
            .mutateAsync({
              schoolId,
              courseId: journal.course.id,
              termId: journal.termId,
              studentId: student.id,
              kind,
              scale,
              value,
              ...(title && { title }),
              ...(existing && { existingId: existing.id }),
            })
            .then(onClose)
        }
      />
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// The journal table
// ---------------------------------------------------------------------------------------------
type Editing =
  | { kind: 'cell'; student: JournalStudent; lesson: JournalLesson }
  | { kind: 'topic'; lesson: JournalLesson }
  | { kind: 'final'; student: JournalStudent; target: 'thematic' | 'semester' };

function GradeChip({ grade }: { grade: GradeRow }) {
  const { t } = useTranslation();
  const value = gradeValue(grade);
  return (
    <Mark
      tone={toneOf(grade.kind, value, grade.special)}
      test={grade.kind === 'test'}
      title={`${t(`journal.kinds.${grade.kind}`)}${grade.title ? ` · ${grade.title}` : ''}: ${gradeLong(t, value)}`}
    >
      {gradeShort(t, value, grade.special)}
    </Mark>
  );
}

/** What the teacher put without connection, and what somebody else put meanwhile. */
function Conflicts({ journal }: { journal: Journal }) {
  const { t } = useTranslation();
  const conflicts = useJournalConflicts(journal.course.id);
  const resolve = useResolveJournalConflict();
  if (conflicts.length === 0) return null;
  const nameOf = (id: string) => {
    const s = journal.students.find((x) => x.id === id);
    return s ? `${s.lastName} ${s.firstName}` : '';
  };
  const dateOf = (id: string) => {
    const l = journal.lessons.find((x) => x.id === id);
    return l ? dayMonth(l.date) : '';
  };
  const describe = (c: JournalConflict, side: 'mine' | 'theirs') => {
    if (c.kind === 'absence') {
      const mark =
        side === 'mine' ? c.action.mark : c.server.kind === 'absence' ? c.server.mark : null;
      return mark ? t(`journal.absenceFull.${mark}`) : t('journal.offline.present');
    }
    const g = side === 'mine' ? c.action.grade : c.server.kind === 'grade' ? c.server.grade : null;
    if (!g) return t('journal.offline.empty');
    const scale = side === 'mine' && c.action.grade ? c.action.grade.scale : 'points12';
    const columns = 'columns' in g ? g.columns : g;
    return (
      gradeShort(t, gradeValue({ scale, ...columns }), columns.special) ||
      t('journal.offline.empty')
    );
  };
  return (
    <div
      className="mx-3 mb-3 flex flex-col gap-2 rounded-[20px] p-4 md:mx-4"
      style={{ backgroundImage: grad('coral'), color: GRAD.coral.ink }}
    >
      <p className="flex items-start gap-2 text-[14.5px] font-medium">
        <WarningCircleIcon size={18} weight="fill" aria-hidden className="mt-px shrink-0" />
        {t('journal.offline.conflicts')}
      </p>
      {conflicts.map((c) => (
        <div
          key={c.id}
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[14px] bg-white/70 px-3 py-2 text-[14px]"
          style={{ color: INK }}
        >
          <span className="min-w-48 flex-1">
            <span className="font-medium">{nameOf(c.action.studentId)}</span> ·{' '}
            {dateOf(c.action.lessonId)} ·{' '}
            {t('journal.offline.mine', { value: describe(c, 'mine') })} ·{' '}
            <span style={{ color: INK_SOFT }}>
              {t('journal.offline.theirs', { value: describe(c, 'theirs') })}
            </span>
          </span>
          <button
            type="button"
            className={buttonClass('primary', 'sm')}
            onClick={() => resolve.keepMine(c)}
          >
            {t('journal.offline.keepMine')}
          </button>
          <button
            type="button"
            className={buttonClass('outline', 'sm')}
            onClick={() => resolve.keepTheirs(c)}
          >
            {t('journal.offline.keepTheirs')}
          </button>
        </div>
      ))}
    </div>
  );
}

const WEEKDAY = new Intl.DateTimeFormat('uk-UA', { timeZone: 'UTC', weekday: 'short' });
const weekdayOf = (iso: string) => WEEKDAY.format(new Date(`${iso}T00:00:00Z`));

function JournalTable({ journal, schoolId }: { journal: Journal; schoolId: string }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Editing>();
  const scroller = useRef<HTMLDivElement>(null);
  const today = todayIso();
  // families' statements: «н» for a reason the school accepted, and days announced ahead
  const notes = useCourseAbsenceNotes(journal.course.id).data ?? [];
  const pending = useJournalPending(journal.course.id);
  const online = useIsOnline();
  const waitingCount = pending.grades.size + pending.absences.size;
  // students who learn from abroad: their clock, and lessons they learn from the materials
  const abroad = new Map((useAbroadStudents(schoolId).data ?? []).map((a) => [a.studentId, a]));
  const now = useMinute();

  const index = useMemo(() => {
    const grades = new Map<string, GradeRow>();
    for (const g of journal.grades)
      if (g.lesson_id) grades.set(`${g.lesson_id}|${g.student_member_id}`, g);
    const absences = new Map(
      journal.attendance.map((a) => [`${a.lesson_id}|${a.student_member_id}`, a.mark]),
    );
    const formative = new Set(
      journal.formative.map((f) => `${f.lesson_id}|${f.student_member_id}`),
    );
    return { grades, absences, formative };
  }, [journal]);

  // the term so far plus the coming week; later lessons would only be empty columns
  const lessons = journal.lessons.filter(
    (l) => l.status === 'scheduled' && l.date <= addDays(today, 7),
  );

  // open at today — that is where the teacher works
  useEffect(() => {
    const el = scroller.current;
    const current = el?.querySelector<HTMLElement>('[data-current]');
    if (!el || !current) return;
    // today's column with two more after it at the right, just before the sticky finals
    const finals = el.querySelector<HTMLElement>('thead th:last-child');
    const right = finals && getComputedStyle(finals).position === 'sticky' ? finals.offsetWidth : 0;
    el.scrollLeft = current.offsetLeft + current.offsetWidth * 3 - (el.clientWidth - right);
  }, [journal.course.id, journal.termId]);

  const look = subjectLook(journal.course.subject);
  const head = (
    <header className="flex items-center gap-3 px-4 pt-4 pb-3 md:px-5 md:pt-5">
      <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={40} />
      <div className="min-w-0 flex-1">
        <p
          className="truncate font-display text-[24px] leading-7 font-light tracking-[-0.03em]"
          style={{ color: INK }}
        >
          {journal.course.subject}
          {journal.course.groupLabel ? ` (${journal.course.groupLabel})` : ''}{' '}
          <span style={{ color: INK_FAINT }}>{journal.course.classLabel}</span>
        </p>
      </div>
    </header>
  );

  if (journal.lessons.length === 0)
    return (
      <section className="rounded-[28px] bg-white pb-2">
        {head}
        <EmptyState>{t('journal.empty')}</EmptyState>
      </section>
    );
  const lastPast = [...lessons].reverse().find((l) => l.date <= today)?.id;
  const todayCol = { backgroundColor: 'rgb(245 243 239 / 0.75)' };
  // the sticky cells keep the row's ground under them
  const sticky = 'bg-white group-hover:bg-[#FAF9F6]';

  return (
    <>
      <section className="min-w-0 rounded-[28px] bg-white">
        {head}
        {(!online || waitingCount > 0) && (
          <p
            className="mx-3 mb-3 flex items-start gap-2 rounded-[16px] px-3.5 py-2.5 text-[14px] leading-5 md:mx-4"
            style={{ backgroundImage: grad('butter'), color: INK }}
          >
            {!online && (
              <WifiOff
                size={17}
                strokeWidth={1.8}
                aria-hidden
                className="mt-px shrink-0"
                style={{ color: GRAD.butter.ink }}
              />
            )}
            <span>
              {!online && `${t('journal.offline.note')} `}
              {waitingCount > 0 && (
                <span className="font-medium" style={{ color: GRAD.butter.ink }}>
                  {t('journal.offline.waitingCount', { count: waitingCount })}
                </span>
              )}
            </span>
          </p>
        )}
        <Conflicts journal={journal} />
        <div ref={scroller} className="overflow-x-auto overscroll-x-contain">
          <table className="border-separate border-spacing-0 text-[14px]" style={{ color: INK }}>
            <thead>
              <tr>
                <th
                  className="sticky left-0 z-20 border-b bg-white py-2 pr-2 pl-4 text-left align-bottom text-[12px] font-medium md:pl-5"
                  style={{ borderColor: LINE, color: INK_FAINT }}
                >
                  {t('journal.student')}
                </th>
                {lessons.map((l) => {
                  const isToday = l.date === today;
                  return (
                    <th
                      key={l.id}
                      {...(l.id === lastPast && { 'data-current': true })}
                      className="border-b p-0 align-bottom font-normal"
                      style={{ borderColor: LINE, ...(isToday ? todayCol : {}) }}
                    >
                      <button
                        type="button"
                        title={l.topic ?? t('journal.topicPlaceholder')}
                        onClick={() => setEditing({ kind: 'topic', lesson: l })}
                        className="flex w-11 flex-col items-center gap-0.5 rounded-[10px] pt-1.5 pb-2 leading-none transition-colors hover:bg-[#EFECE5]"
                      >
                        <span
                          className="text-[10.5px]"
                          style={{ color: isToday ? TODAY_INK : INK_FAINT }}
                        >
                          {weekdayOf(l.date)}
                        </span>
                        <span
                          className={`text-[12.5px] tabular-nums ${isToday ? 'font-medium' : ''}`}
                          style={{
                            color: isToday ? TODAY_INK : l.date > today ? INK_FAINT : INK_SOFT,
                          }}
                        >
                          {dayMonth(l.date)}
                        </span>
                        <span className="flex h-1.5 items-center gap-[3px]" aria-hidden>
                          {l.isTest && (
                            <span
                              className="size-1.5 rounded-full"
                              style={{ backgroundColor: TEST_DOT }}
                            />
                          )}
                          {l.format !== 'in_person' && (
                            <span
                              className="size-1.5 rounded-full"
                              style={{ backgroundColor: FORMAT_DOT[l.format] }}
                            />
                          )}
                        </span>
                      </button>
                    </th>
                  );
                })}
                <th
                  className="z-10 border-b border-l bg-white p-0 align-bottom text-[12px] font-medium sm:sticky sm:right-0"
                  style={{ borderColor: LINE, color: INK_FAINT }}
                >
                  <span className="flex items-end py-2 pr-3">
                    <span className={`${FINALS.thematic} text-left`}>{t('journal.thematic')}</span>
                    <span className={`${FINALS.average} text-center`}>{t('journal.average')}</span>
                    <span className={`${FINALS.semester} text-center`}>
                      {t('journal.semester')}
                    </span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {journal.students.map((s) => {
                const home = abroad.get(s.id);
                const mine = journal.grades.filter((g) => g.student_member_id === s.id);
                const thematic = mine.filter((g) => g.kind === 'thematic');
                const semester = mine.find((g) => g.kind === 'semester');
                const points = mine
                  .filter((g) => g.kind === 'current' || g.kind === 'test')
                  .map((g) => g.points)
                  .filter((p): p is number => p !== null);
                const average = points.length
                  ? (points.reduce((a, b) => a + b, 0) / points.length).toFixed(1)
                  : '';
                return (
                  <tr key={s.id} className="group">
                    <td
                      className={`${sticky} sticky left-0 z-10 max-w-[148px] border-b py-1 pr-2 pl-4 sm:max-w-none md:pl-5`}
                      style={{ borderColor: LINE }}
                    >
                      <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
                        <span className="min-w-0 truncate">
                          {s.lastName} {s.firstName}
                        </span>
                        {home && (
                          <span
                            className="shrink-0 cursor-help rounded-[6px] px-1.5 py-0.5 text-[11px] leading-none font-medium"
                            style={{ backgroundImage: grad('sky'), color: GRAD.sky.ink }}
                            title={t('abroad.journalHint', {
                              place: placeOf(home),
                              time: thereNow(t, home.timezone, now),
                            })}
                          >
                            {/* the city without the flag: no emoji in the interface */}
                            {placeOf(home).replace(/^\S+\s/, '')}
                          </span>
                        )}
                      </span>
                    </td>
                    {lessons.map((l) => {
                      const key = `${l.id}|${s.id}`;
                      // a change made without connection shows at once, dashed, until it is sent
                      const queuedGrade = pending.grades.get(key);
                      const queuedAbsence = pending.absences.get(key);
                      const waiting = Boolean(queuedGrade || queuedAbsence);
                      const grade = queuedGrade
                        ? queuedGrade.grade &&
                          ({
                            ...index.grades.get(key),
                            kind: queuedGrade.grade.kind,
                            scale: queuedGrade.grade.scale,
                            title: queuedGrade.grade.title ?? null,
                            ...queuedGrade.grade.columns,
                          } as GradeRow)
                        : index.grades.get(key);
                      const absence = queuedAbsence ? queuedAbsence.mark : index.absences.get(key);
                      const covered = isCovered(notes, s.id, l.date);
                      const fit = home ? lessonFit(l, home) : 'live';
                      return (
                        <td
                          key={l.id}
                          className="border-b p-0 text-center group-hover:bg-[#FAF9F6]"
                          style={{ borderColor: LINE, ...(l.date === today ? todayCol : {}) }}
                        >
                          <button
                            type="button"
                            disabled={l.date > today}
                            onClick={() => setEditing({ kind: 'cell', student: s, lesson: l })}
                            className="relative flex h-9 w-11 items-center justify-center rounded-[10px] transition-colors hover:bg-[#EFECE5] disabled:cursor-default disabled:hover:bg-transparent"
                          >
                            {absence ? (
                              <span
                                className="inline-flex items-center gap-px text-[13px] font-medium"
                                style={{ color: covered ? GRAD.mint.ink : GRAD.coral.ink }}
                                title={covered ? t('requests.covered') : undefined}
                              >
                                {t(`journal.absence.${absence}`)}
                                {covered && <Check size={11} strokeWidth={2.4} aria-hidden />}
                              </span>
                            ) : grade ? (
                              <GradeChip grade={grade} />
                            ) : covered && l.date >= today ? (
                              <span
                                className="text-[10.5px] font-medium"
                                style={{ color: GRAD.mint.ink }}
                                title={t('requests.covered')}
                              >
                                {t('journal.announcedAbsence')}
                              </span>
                            ) : fit !== 'live' ? (
                              <span
                                className="text-[10.5px]"
                                style={{ color: INK_FAINT }}
                                title={t('abroad.asyncTitle', {
                                  why: t(`abroad.asyncWhy.${fit}`),
                                })}
                              >
                                {t('abroad.asyncCell')}
                              </span>
                            ) : null}
                            {index.formative.has(key) && (
                              <span
                                aria-hidden
                                className="absolute right-1 bottom-1 size-1.5 rounded-full"
                                style={{ backgroundColor: FEEDBACK_DOT }}
                              />
                            )}
                            {waiting && (
                              <span
                                title={t('journal.offline.waiting')}
                                className="absolute inset-0.5 rounded-[9px] border border-dashed"
                                style={{ borderColor: 'rgb(31 26 53 / 0.4)' }}
                              />
                            )}
                          </button>
                        </td>
                      );
                    })}
                    <td
                      className={`${sticky} z-10 border-b border-l p-0 sm:sticky sm:right-0`}
                      style={{ borderColor: LINE }}
                    >
                      <span className="flex items-center pr-3">
                        <span className={`${FINALS.thematic} px-1.5`}>
                          <button
                            type="button"
                            aria-label={`${t('journal.kinds.thematic')} · ${s.lastName} ${s.firstName}`}
                            className="flex min-h-9 w-full flex-wrap items-center gap-1 rounded-[10px] px-0.5 py-1 transition-colors hover:bg-[#EFECE5]"
                            onClick={() =>
                              setEditing({ kind: 'final', student: s, target: 'thematic' })
                            }
                          >
                            {thematic.map((g) => (
                              <GradeChip key={g.id} grade={g} />
                            ))}
                            <span
                              className="px-1 text-[15px] leading-none"
                              style={{ color: INK_FAINT }}
                            >
                              +
                            </span>
                          </button>
                        </span>
                        <span
                          className={`${FINALS.average} text-center font-display text-[15px] font-light tabular-nums`}
                        >
                          {average.replace('.', ',')}
                        </span>
                        <span className={FINALS.semester}>
                          <button
                            type="button"
                            aria-label={`${t('journal.kinds.semester')} · ${s.lastName} ${s.firstName}`}
                            className="flex h-9 w-full items-center justify-center rounded-[10px] transition-colors hover:bg-[#EFECE5]"
                            onClick={() =>
                              setEditing({ kind: 'final', student: s, target: 'semester' })
                            }
                          >
                            {semester ? (
                              <GradeChip grade={semester} />
                            ) : (
                              <span className="text-[13px]" style={{ color: INK_FAINT }}>
                                …
                              </span>
                            )}
                          </button>
                        </span>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* the legend: the marks as they are in the table */}
        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pt-3 pb-4 text-[12px] md:px-5"
          style={{ color: INK_SOFT }}
        >
          <span className="inline-flex items-center gap-1">
            {(['1–3', '4–6', '7–9', '10–12'] as const).map((range, i) => (
              <Mark key={range} tone={LEVEL[i] as Grad} size="xs">
                {range}
              </Mark>
            ))}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Mark tone="lavender" size="xs">
              9
            </Mark>
            {t('journal.kinds.thematic')}, {t('journal.kinds.semester').toLowerCase()}
          </span>
          <Dot color={TEST_DOT}>{t('journal.kinds.test')}</Dot>
          <Dot color={FEEDBACK_DOT}>{t('journal.editor.formative')}</Dot>
          <Dot color={FORMAT_DOT.online}>{t('format.online')}</Dot>
          <Dot color={FORMAT_DOT.shelter}>{t('format.shelter')}</Dot>
          <span>
            <span style={{ color: GRAD.coral.ink }}>{t('journal.legend')}</span>
            {abroad.size > 0 &&
              ` · ${t('abroad.asyncCell')} — ${t('abroad.fit.school')} / ${t('abroad.fit.night')}`}
          </span>
        </div>
      </section>

      {editing?.kind === 'cell' && (
        <CellEditor
          journal={journal}
          schoolId={schoolId}
          student={editing.student}
          lesson={editing.lesson}
          onClose={() => setEditing(undefined)}
        />
      )}
      {editing?.kind === 'topic' && (
        <TopicEditor
          journal={journal}
          lesson={editing.lesson}
          onClose={() => setEditing(undefined)}
        />
      )}
      {editing?.kind === 'final' && (
        <FinalEditor
          journal={journal}
          schoolId={schoolId}
          student={editing.student}
          kind={editing.target}
          onClose={() => setEditing(undefined)}
        />
      )}
    </>
  );
}

export function JournalPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const picker = useCoursePicker();
  const terms = useTerms(ctx.schoolId);
  const [termId, setTermId] = useState<string>();
  const course = picker.course;
  const term =
    terms.data?.find((x) => x.id === termId) ?? (terms.data && termFor(terms.data, todayIso()));
  const journal = useJournal(course?.id, term?.id);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <PageHead title={t('journal.title')}>
        {/* bounded, so on a phone the choices wrap under the title instead of widening the page */}
        <div className="flex max-w-[calc(100vw-32px)] flex-wrap items-center gap-2">
          {picker.controls}
          {terms.data && terms.data.length > 1 && (
            <Tabs
              label={t('journal.term', { n: '' })}
              value={term?.id}
              onChange={setTermId}
              options={terms.data.map((x) => ({
                id: x.id,
                label: t('journal.term', { n: x.number }),
              }))}
            />
          )}
        </div>
      </PageHead>
      {picker.loading || terms.isPending ? (
        <Spinner label={t('common.loading')} />
      ) : !course ? (
        <section className="rounded-[28px] bg-white p-6">
          <EmptyState>{t('journal.noCourses')}</EmptyState>
        </section>
      ) : journal.isPending ? (
        <Spinner label={t('common.loading')} />
      ) : journal.isError ? (
        <EmptyState>{t('common.error')}</EmptyState>
      ) : (
        <JournalTable journal={journal.data} schoolId={ctx.schoolId} />
      )}
    </div>
  );
}
